import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Settings, Save, Plus, Trash2, Edit, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ExamConfigTab({ token }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);

  useEffect(() => {
    fetchConfigs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/admin/exam-configurations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!editingConfig) return;
    
    // Validate total weightage
    const total = editingConfig.components.reduce((sum, c) => sum + c.weightage, 0);
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Total weightage must equal 100%. Current: ${total}%`);
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/exam-configurations/${editingConfig.examination_code}`,
        editingConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuration saved successfully');
      setEditingConfig(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const updateComponentWeightage = (idx, field, value) => {
    const updated = { ...editingConfig };
    updated.components[idx][field] = field === 'is_safety_critical' ? value : parseFloat(value) || 0;
    setEditingConfig(updated);
  };

  const componentNames = {
    mcq: 'MCQ Written Exam',
    case_log: 'Case Log Portfolio',
    osce: 'OSCE Clinical Skills',
    viva: 'Viva Voce',
    ethics: 'Ethics & Governance'
  };

  const levelLabels = {
    assistant: 'Assistant Level',
    board_certification: 'Board Certification',
    fellowship: 'Fellowship'
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="admin-exam-config-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-[#1B2A4A]">Examination Configurations</h3>
        <p className="text-xs text-slate-500">Configure weightages and passing thresholds for each examination</p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="text-xs font-bold">Code</TableHead>
              <TableHead className="text-xs font-bold">Examination</TableHead>
              <TableHead className="text-xs font-bold">Level</TableHead>
              <TableHead className="text-xs font-bold">Components</TableHead>
              <TableHead className="text-xs font-bold">Pass %</TableHead>
              <TableHead className="text-xs font-bold">Status</TableHead>
              <TableHead className="text-xs font-bold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config.examination_code}>
                <TableCell className="font-mono font-bold text-[#1B2A4A]">{config.examination_code}</TableCell>
                <TableCell className="text-sm">{config.examination_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs rounded-sm">
                    {levelLabels[config.level] || config.level}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {config.components?.map((c, i) => (
                      <Badge key={i} className={`text-[10px] ${c.is_safety_critical ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                        {componentNames[c.component]?.split(' ')[0]} {c.weightage}%
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="font-medium">{config.overall_passing_threshold}%</TableCell>
                <TableCell>
                  <Badge className={`text-xs ${config.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                    {config.active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs rounded-sm"
                    onClick={() => setEditingConfig({ ...config })}
                  >
                    <Edit className="h-3 w-3 mr-1" /> Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Modal */}
      {editingConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingConfig(null)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Edit {editingConfig.examination_code}</h3>
                  <p className="text-sm text-slate-500">{editingConfig.examination_name}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingConfig(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Component Weightages */}
              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-[#1B2A4A]">Component Weightages</h4>
                {editingConfig.components?.map((comp, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-sm">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-[#1B2A4A]">{componentNames[comp.component]}</span>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={comp.is_safety_critical}
                          onChange={(e) => updateComponentWeightage(idx, 'is_safety_critical', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-red-600">Safety Critical</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Weightage (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={comp.weightage}
                          onChange={(e) => updateComponentWeightage(idx, 'weightage', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Passing Threshold (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={comp.passing_threshold}
                          onChange={(e) => updateComponentWeightage(idx, 'passing_threshold', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      {comp.component === 'case_log' && (
                        <div>
                          <Label className="text-xs">Min Cases</Label>
                          <Input
                            type="number"
                            min="0"
                            value={comp.min_cases || ''}
                            onChange={(e) => updateComponentWeightage(idx, 'min_cases', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                      {comp.component === 'mcq' && (
                        <div>
                          <Label className="text-xs">Min Questions</Label>
                          <Input
                            type="number"
                            min="0"
                            value={comp.min_questions || ''}
                            onChange={(e) => updateComponentWeightage(idx, 'min_questions', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Total Weightage Display */}
                <div className={`p-3 rounded-sm text-center ${
                  Math.abs(editingConfig.components?.reduce((sum, c) => sum + c.weightage, 0) - 100) < 0.01
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}>
                  <p className="font-semibold">
                    Total Weightage: {editingConfig.components?.reduce((sum, c) => sum + c.weightage, 0)}%
                    {Math.abs(editingConfig.components?.reduce((sum, c) => sum + c.weightage, 0) - 100) >= 0.01 && (
                      <span className="text-red-600 ml-2">(Must equal 100%)</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Award Thresholds */}
              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-[#1B2A4A]">Award Classification Thresholds</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Overall Passing (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editingConfig.overall_passing_threshold}
                      onChange={(e) => setEditingConfig({ ...editingConfig, overall_passing_threshold: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">Certified</p>
                  </div>
                  <div>
                    <Label className="text-xs">Merit Threshold (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editingConfig.merit_threshold}
                      onChange={(e) => setEditingConfig({ ...editingConfig, merit_threshold: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">Certified with Merit</p>
                  </div>
                  <div>
                    <Label className="text-xs">Distinction Threshold (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editingConfig.distinction_threshold}
                      onChange={(e) => setEditingConfig({ ...editingConfig, distinction_threshold: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">Certified with Distinction</p>
                  </div>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2 mb-6">
                <input
                  type="checkbox"
                  checked={editingConfig.active}
                  onChange={(e) => setEditingConfig({ ...editingConfig, active: e.target.checked })}
                  className="rounded"
                />
                <Label>Active (available for new examinations)</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingConfig(null)}>Cancel</Button>
                <Button className="bg-[#1B2A4A]" onClick={saveConfig}>Save Configuration</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ExamConfigTab;
